import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import axios from "axios";
import { toast } from "sonner";
import { 
  Film, 
  Upload, 
  BookOpen, 
  Plus, 
  Trash2, 
  Loader2, 
  FileText,
  Users,
  Clock,
  Clapperboard,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Copy
} from "lucide-react";
import Sidebar from "@/components/Sidebar";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ScriptCreator = () => {
  const navigate = useNavigate();
  const [scripts, setScripts] = useState([]);
  const [completedBooks, setCompletedBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userCredits, setUserCredits] = useState(0);
  
  // Dialog states
  const [showNewScript, setShowNewScript] = useState(false);
  const [showScriptDetail, setShowScriptDetail] = useState(false);
  const [selectedScript, setSelectedScript] = useState(null);
  
  // Form states
  const [sourceType, setSourceType] = useState("dtr_book"); // "dtr_book" or "upload"
  const [selectedBookId, setSelectedBookId] = useState("");
  const [scriptTitle, setScriptTitle] = useState("");
  const [adaptationStyle, setAdaptationStyle] = useState("faithful");
  const [sceneCount, setSceneCount] = useState(60); // 30, 60, or 80 scenes
  const [uploadedFile, setUploadedFile] = useState(null);
  const [converting, setConverting] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  useEffect(() => {
    loadData();
  }, []);

  // Auto-refresh when viewing a generating script
  useEffect(() => {
    let interval;
    if (showScriptDetail && selectedScript?.status === "generating") {
      interval = setInterval(() => {
        refreshScript(selectedScript.id);
      }, 3000); // Refresh every 3 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [showScriptDetail, selectedScript?.status, selectedScript?.id]);

  // Auto-refresh script list when there are generating scripts
  useEffect(() => {
    let interval;
    const hasGenerating = scripts.some(s => s.status === "generating");
    if (hasGenerating && !showScriptDetail) {
      interval = setInterval(() => {
        loadData();
      }, 5000); // Refresh list every 5 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [scripts, showScriptDetail]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [scriptsRes, booksRes, creditsRes] = await Promise.all([
        axios.get(`${API}/scripts`, config),
        axios.get(`${API}/books/completed`, config),
        axios.get(`${API}/credits`, config)
      ]);
      
      // DEBUG: Log what the API returns
      console.log("API /scripts returned:", scriptsRes.data);
      console.log("Scripts count from API:", scriptsRes.data?.length || 0);
      
      const fetchedScripts = scriptsRes.data || [];
      
      // Force empty if API says empty
      if (fetchedScripts.length === 0) {
        console.log("API returned empty scripts - clearing UI");
        setScripts([]);
        setSelectedScript(null);
        setShowScriptDetail(false);
      } else {
        setScripts(fetchedScripts);
      }
      
      setCompletedBooks(booksRes.data || []);
      setUserCredits(creditsRes.data?.credits || 0);
      
      // If viewing a script detail that no longer exists, close the dialog
      if (selectedScript && showScriptDetail) {
        const stillExists = fetchedScripts.some(s => s.id === selectedScript.id);
        if (!stillExists) {
          setShowScriptDetail(false);
          setSelectedScript(null);
          toast.info("The screenplay you were viewing no longer exists.");
        }
      }
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File is too large. Maximum size is 10MB.");
        event.target.value = ''; // Reset input
        return;
      }
      
      const allowedTypes = ['.txt', '.docx', '.pdf'];
      const fileExt = '.' + file.name.split('.').pop().toLowerCase();
      
      // Safari sometimes doesn't report correct file type, so check extension
      if (!allowedTypes.includes(fileExt)) {
        toast.error("Please upload a .txt, .docx, or .pdf file");
        event.target.value = ''; // Reset input
        return;
      }
      
      setUploadedFile(file);
      if (!scriptTitle) {
        setScriptTitle(file.name.replace(/\.[^/.]+$/, ""));
      }
      toast.success(`File "${file.name}" selected`);
    }
  };

  const handleConvert = async () => {
    if (!scriptTitle.trim()) {
      toast.error("Please enter a script title");
      return;
    }
    
    if (!userCredits || userCredits < 5) {
      toast.error(`Insufficient credits. You have ${userCredits || 0} credits but need 5.`);
      return;
    }
    
    if (sourceType === "dtr_book" && !selectedBookId) {
      toast.error("Please select a book to convert");
      return;
    }
    
    if (sourceType === "upload" && !uploadedFile) {
      toast.error("Please upload a book file");
      return;
    }
    
    setConverting(true);
    
    try {
      const token = localStorage.getItem("token");
      let response;
      
      if (sourceType === "dtr_book") {
        response = await axios.post(
          `${API}/scripts/convert-from-book`,
          {
            title: scriptTitle,
            adaptation_style: adaptationStyle,
            source_book_id: selectedBookId,
            scene_count: sceneCount
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        const formData = new FormData();
        // For Safari/iOS compatibility, create a new Blob with explicit type
        const fileBlob = new Blob([uploadedFile], { type: uploadedFile.type || 'application/octet-stream' });
        formData.append('file', fileBlob, uploadedFile.name);
        formData.append('title', scriptTitle);
        formData.append('adaptation_style', adaptationStyle);
        formData.append('scene_count', sceneCount.toString());
        
        response = await axios.post(
          `${API}/scripts/convert-upload`,
          formData,
          { 
            headers: { 
              Authorization: `Bearer ${token}`
              // Don't set Content-Type - let browser set it with boundary
            },
            timeout: 120000 // 2 minute timeout 
          }
        );
      }
      
      toast.success("Screenplay conversion started! This may take several minutes.");
      setShowNewScript(false);
      resetForm();
      
      // Reload data to show the new script
      await loadData();
      
    } catch (error) {
      console.error("Conversion error:", error);
      toast.error(error.response?.data?.detail || "Failed to start conversion");
    } finally {
      setConverting(false);
    }
  };

  const resetForm = () => {
    setSourceType("dtr_book");
    setSelectedBookId("");
    setScriptTitle("");
    setAdaptationStyle("faithful");
    setSceneCount(60);
    setUploadedFile(null);
  };

  const handleViewScript = async (script) => {
    // Navigate to dedicated detail page (like BookDetail)
    navigate(`/script/${script.id}`);
  };

  const handleCopyScreenplay = async () => {
    if (!selectedScript) {
      toast.error("No script selected");
      return;
    }
    
    if (!selectedScript.scenes || selectedScript.scenes.length === 0) {
      toast.error("No screenplay content to copy - scenes are empty");
      return;
    }

    toast.info('Preparing screenplay content...');

    // Format the screenplay as text
    let screenplayText = `${selectedScript.title?.toUpperCase() || 'UNTITLED'}\n`;
    screenplayText += `${'='.repeat(selectedScript.title?.length || 8)}\n\n`;
    screenplayText += `Written by AI Screenplay Adaptation\n\n`;

    for (const scene of selectedScript.scenes) {
      screenplayText += `${scene.heading?.toUpperCase() || 'INT. LOCATION - DAY'}\n\n`;
      screenplayText += `${scene.content || ''}\n\n`;
      screenplayText += `${'─'.repeat(50)}\n\n`;
    }

    console.log('[Copy] Text length:', screenplayText.length);

    // Try modern clipboard API first (same as BookDetail)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(screenplayText);
        console.log('[Copy] Successfully copied via Clipboard API');
        toast.success(`Copied ${selectedScript.scenes.length} scenes to clipboard! Paste into Word.`);
        return;
      } catch (clipboardError) {
        console.warn('[Copy] Clipboard API failed, trying fallback:', clipboardError);
      }
    }

    // Fallback method using textarea (exactly like BookDetail)
    const textarea = document.createElement('textarea');
    textarea.value = screenplayText;
    textarea.style.position = 'fixed';
    textarea.style.top = '0';
    textarea.style.left = '0';
    textarea.style.width = '2em';
    textarea.style.height = '2em';
    textarea.style.padding = '0';
    textarea.style.border = 'none';
    textarea.style.outline = 'none';
    textarea.style.boxShadow = 'none';
    textarea.style.background = 'transparent';
    
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textarea);
    
    if (successful) {
      console.log('[Copy] Successfully copied via fallback method');
      toast.success(`Copied ${selectedScript.scenes.length} scenes to clipboard! Paste into Word.`);
    } else {
      toast.error("Copy command failed. Try selecting text manually.");
    }
  };

  const handleDeleteScript = async (scriptId) => {
    const confirmed = window.confirm("Delete this screenplay?");
    if (!confirmed) return;
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(`${API}/scripts/${scriptId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Deleted!");
      await loadData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleResumeScript = async (scriptId) => {
    const confirmed = window.confirm("Resume generation?");
    if (!confirmed) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/scripts/${scriptId}/resume`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Resumed!");
      await loadData();
    } catch (error) {
      toast.error("Failed to resume");
    }
  };

  const refreshScript = async (scriptId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/scripts/${scriptId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedScript(response.data);
      await loadData();
    } catch (error) {
      console.error("Failed to refresh script:", error);
      // If script not found, close dialog and refresh list
      if (error.response?.status === 404) {
        setShowScriptDetail(false);
        setSelectedScript(null);
        await loadData();
        toast.info("Script no longer exists. List refreshed.");
      }
    }
  };

  const handleManualRefresh = async () => {
    toast.success("Refreshing scripts...");
    // Force close any open dialogs
    setShowScriptDetail(false);
    setSelectedScript(null);
    await loadData();
  };

  const handleClearStuck = async () => {
    // Force clear all state
    setScripts([]);
    setSelectedScript(null);
    setShowScriptDetail(false);
    setShowNewScript(false);
    
    // Clear any browser caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }
    
    // Clear session storage
    sessionStorage.clear();
    
    toast.success("Cache cleared! Redirecting...");
    
    // Navigate away and back to force fresh load
    setTimeout(() => {
      window.location.href = '/dashboard';
      setTimeout(() => {
        window.location.href = '/script-creator';
      }, 100);
    }, 500);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" /> Completed</Badge>;
      case "generating":
        return <Badge className="bg-blue-100 text-blue-800"><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Generating</Badge>;
      case "error":
        return <Badge className="bg-red-100 text-red-800"><AlertCircle className="h-3 w-3 mr-1" /> Error</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-stone-50 to-neutral-100">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-lg font-medium text-stone-700">Loading Script Creator...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-stone-50 to-neutral-100 flex">
      <Sidebar onLogout={handleLogout} />
      
      <div className="flex-1 ml-0 md:ml-64 py-6 md:py-12 px-4 md:px-8">
        <div className="container mx-auto max-w-7xl">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-4xl font-bold playfair text-stone-900 mb-2 flex items-center gap-3">
                <Clapperboard className="h-8 w-8 md:h-10 md:w-10 text-amber-600" />
                Script Creator
              </h1>
              <p className="text-sm md:text-base text-stone-600">
                Convert your books into Hollywood-standard screenplays
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 md:gap-4">
              <Button 
                variant="outline"
                onClick={handleManualRefresh}
                className="rounded-full"
                size="sm"
              >
                <RefreshCw className="h-4 w-4 mr-1 md:mr-2" />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
              <Button 
                variant="ghost"
                onClick={handleClearStuck}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                size="sm"
              >
                <span className="hidden sm:inline">Clear Stuck</span>
                <span className="sm:hidden">Clear</span>
              </Button>
              <Badge variant="outline" className="text-sm md:text-lg px-2 md:px-4 py-1 md:py-2">
                {userCredits} Credits
              </Badge>
              <Button 
                onClick={() => setShowNewScript(true)} 
                size="sm"
                className="bg-amber-900 hover:bg-amber-800 text-white rounded-full shadow-lg md:text-base"
              >
                <Plus className="h-4 w-4 md:h-5 md:w-5 mr-1 md:mr-2" />
                <span className="hidden sm:inline">New Screenplay</span>
                <span className="sm:hidden">New</span>
              </Button>
            </div>
          </div>

          {/* Info Card */}
          <Card className="mb-8 border-amber-200 bg-amber-50/50">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-start gap-3 md:gap-4">
                <Film className="h-6 w-6 md:h-8 md:w-8 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-base md:text-lg text-stone-900 mb-2">Book to Screenplay Conversion</h3>
                  <p className="text-sm md:text-base text-stone-600 mb-3">
                    Transform your novels into industry-standard Hollywood screenplays. 
                    Our AI adapts your story into proper screenplay format with scene headings, 
                    action lines, dialogue, and professional formatting.
                  </p>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>Scene Breakdown</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>Character List</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>Copy to Clipboard</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>Industry-Standard Format</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Scripts Grid */}
          {scripts.length === 0 ? (
            <Card className="text-center py-16 border-stone-200 shadow-lg">
              <CardContent>
                <Film className="h-20 w-20 mx-auto text-stone-400 mb-4" />
                <h3 className="text-2xl font-semibold playfair text-stone-900 mb-2">No screenplays yet</h3>
                <p className="text-stone-600 mb-6">
                  Convert your first book into a Hollywood-format screenplay
                </p>
                <Button 
                  onClick={() => setShowNewScript(true)}
                  className="bg-amber-900 hover:bg-amber-800 text-white rounded-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Screenplay
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {scripts.map((script) => (
                <Card 
                  key={script.id} 
                  className="hover:shadow-xl transition-all border-stone-200"
                >
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-xl playfair flex items-center gap-2">
                          <FileText className="h-5 w-5 text-amber-600" />
                          {script.title}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          {script.source_type === "dtr_book" ? "From DTR Book" : "Uploaded Book"}
                          <span className="ml-2 text-xs text-gray-400">(ID: {script.id || "MISSING"})</span>
                        </CardDescription>
                      </div>
                      {getStatusBadge(script.status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Progress bar for generating scripts */}
                    {script.status === "generating" && (
                      <div className="mb-4">
                        <div className="w-full bg-stone-200 rounded-full h-2 mb-1">
                          <div 
                            className="bg-amber-600 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${script.progress || 0}%` }}
                          />
                        </div>
                        <p className="text-xs text-stone-500">
                          {script.progress || 0}% - {script.progress_message || "Processing..."}
                        </p>
                      </div>
                    )}
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-stone-600 flex items-center gap-1">
                          <Clapperboard className="h-4 w-4" /> Scenes
                        </span>
                        <span className="font-medium">
                          {script.status === "generating" 
                            ? `${script.scenes_completed || 0}/${script.total_scenes || "?"}` 
                            : script.scene_count || 0}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-stone-600 flex items-center gap-1">
                          <Users className="h-4 w-4" /> Characters
                        </span>
                        <span className="font-medium">{script.characters?.length || 0}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-stone-600 flex items-center gap-1">
                          <Clock className="h-4 w-4" /> Est. Runtime
                        </span>
                        <span className="font-medium">{script.estimated_runtime || "N/A"}</span>
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t gap-2">
                        {(script.status === "failed" || script.status === "generating") && (
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-md text-sm font-medium border border-input shadow-sm hover:bg-accent hover:text-accent-foreground h-8 px-3"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              console.log("Card Resume clicked", script.id);
                              handleResumeScript(script.id);
                            }}
                          >
                            Resume
                          </button>
                        )}
                        <button
                          type="button"
                          className="inline-flex items-center justify-center rounded-md text-sm font-medium border border-input shadow-sm hover:bg-accent hover:text-accent-foreground h-8 px-3"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log("Card Delete clicked", script.id);
                            handleDeleteScript(script.id);
                          }}
                        >
                          Delete
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center rounded-md text-sm font-medium border border-input shadow-sm hover:bg-accent hover:text-accent-foreground h-8 px-3"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log("Card View clicked", script.id);
                            handleViewScript(script);
                          }}
                        >
                          View
                        </button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* New Screenplay Dialog */}
          <Dialog open={showNewScript} onOpenChange={setShowNewScript}>
            <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
              <DialogHeader>
                <DialogTitle className="text-2xl flex items-center gap-2">
                  <Film className="h-6 w-6 text-amber-600" />
                  Create New Screenplay
                </DialogTitle>
                <DialogDescription>
                  Convert your book into a Hollywood-format screenplay (5 credits)
                </DialogDescription>
              </DialogHeader>
              
              <div className="flex-1 overflow-y-auto space-y-6 pr-2">
                {/* Source Selection */}
                <div className="space-y-3">
                  <Label className="text-base font-semibold">Book Source</Label>
                  <Tabs value={sourceType} onValueChange={setSourceType}>
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="dtr_book" className="flex items-center gap-2">
                        <BookOpen className="h-4 w-4" />
                        DTR Book
                      </TabsTrigger>
                      <TabsTrigger value="upload" className="flex items-center gap-2">
                        <Upload className="h-4 w-4" />
                        Upload Book
                      </TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="dtr_book" className="mt-4">
                      {completedBooks.length === 0 ? (
                        <div className="text-center py-8 text-stone-500">
                          <BookOpen className="h-12 w-12 mx-auto mb-2 text-stone-400" />
                          <p>No completed books found.</p>
                          <p className="text-sm">Complete a book first or upload an external book.</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Label>Select a completed book</Label>
                          <Select value={selectedBookId} onValueChange={setSelectedBookId}>
                            <SelectTrigger>
                              <SelectValue placeholder="Choose a book..." />
                            </SelectTrigger>
                            <SelectContent>
                              {completedBooks.map((book) => (
                                <SelectItem key={book.id} value={book.id}>
                                  {book.title} ({book.total_chapters} chapters)
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <div className="p-3 bg-green-50 border border-green-200 rounded-lg mt-3">
                            <p className="text-sm text-green-700 flex items-center gap-2 font-medium">
                              <CheckCircle className="h-4 w-4" />
                              Special Deal: 5 credits covers your Book + Screenplay!
                            </p>
                            <p className="text-xs text-green-600 mt-1 ml-6">
                              Since you&apos;re using a DTR book, the script price includes your book cost.
                            </p>
                          </div>
                        </div>
                      )}
                    </TabsContent>
                    
                    <TabsContent value="upload" className="mt-4">
                      <div className="space-y-4">
                        <div className="border-2 border-dashed border-stone-300 rounded-lg p-8 text-center hover:border-amber-400 transition-colors">
                          <input
                            type="file"
                            accept=".txt,.docx,.pdf"
                            onChange={handleFileUpload}
                            className="hidden"
                            id="book-upload"
                          />
                          <label htmlFor="book-upload" className="cursor-pointer">
                            <Upload className="h-12 w-12 mx-auto text-stone-400 mb-3" />
                            <p className="text-stone-600 font-medium">
                              {uploadedFile ? uploadedFile.name : "Click to upload your book"}
                            </p>
                            <p className="text-sm text-stone-500 mt-1">
                              Supports .txt, .docx, .pdf
                            </p>
                          </label>
                        </div>
                        <p className="text-sm text-stone-500">
                          External books: 5 credits for screenplay conversion
                        </p>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>

                {/* Script Title */}
                <div className="space-y-2">
                  <Label htmlFor="script-title">Screenplay Title</Label>
                  <Input
                    id="script-title"
                    placeholder="Enter the screenplay title"
                    value={scriptTitle}
                    onChange={(e) => setScriptTitle(e.target.value)}
                  />
                </div>

                {/* Adaptation Style */}
                <div className="space-y-2">
                  <Label>Adaptation Style</Label>
                  <Select value={adaptationStyle} onValueChange={setAdaptationStyle}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="faithful">
                        <div className="flex flex-col">
                          <span className="font-medium">Faithful Adaptation</span>
                          <span className="text-xs text-stone-500">Keep all major plot points and characters</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="condensed">
                        <div className="flex flex-col">
                          <span className="font-medium">Condensed Adaptation</span>
                          <span className="text-xs text-stone-500">Focus on main plot, streamline subplots</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Scene Count Selection */}
                <div className="space-y-3">
                  <Label className="text-base font-semibold">Number of Scenes</Label>
                  <p className="text-sm text-stone-500">
                    Choose how many scenes your screenplay should have
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={() => setSceneCount(30)}
                      className={`p-4 rounded-lg border-2 transition-all text-center ${
                        sceneCount === 30
                          ? "border-amber-600 bg-amber-50 text-amber-900"
                          : "border-stone-200 hover:border-amber-300 bg-white"
                      }`}
                    >
                      <span className="block text-2xl font-bold">30</span>
                      <span className="block text-xs text-stone-500 mt-1">Short Film</span>
                      <span className="block text-xs text-stone-400">~45-60 pages</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setSceneCount(60)}
                      className={`p-4 rounded-lg border-2 transition-all text-center relative ${
                        sceneCount === 60
                          ? "border-amber-600 bg-amber-50 text-amber-900"
                          : "border-stone-200 hover:border-amber-300 bg-white"
                      }`}
                    >
                      <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-amber-600 text-white text-xs">
                        Recommended
                      </Badge>
                      <span className="block text-2xl font-bold mt-1">60</span>
                      <span className="block text-xs text-stone-500 mt-1">Feature Film</span>
                      <span className="block text-xs text-stone-400">~90-120 pages</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setSceneCount(80)}
                      className={`p-4 rounded-lg border-2 transition-all text-center ${
                        sceneCount === 80
                          ? "border-amber-600 bg-amber-50 text-amber-900"
                          : "border-stone-200 hover:border-amber-300 bg-white"
                      }`}
                    >
                      <span className="block text-2xl font-bold">80</span>
                      <span className="block text-xs text-stone-500 mt-1">Epic</span>
                      <span className="block text-xs text-stone-400">~120-150 pages</span>
                    </button>
                  </div>
                </div>

                {/* Credit Info */}
                <div className="p-4 bg-stone-100 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Cost:</span>
                    <span className="text-lg font-bold text-amber-700">5 Credits</span>
                  </div>
                  <div className="flex justify-between items-center mt-1 text-sm text-stone-600">
                    <span>Your balance:</span>
                    <span>{userCredits} Credits</span>
                  </div>
                  {(userCredits === null || userCredits === undefined || userCredits < 5) && (
                    <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {userCredits === null || userCredits === undefined 
                        ? "Loading credits..." 
                        : "Insufficient credits - need 5 credits"}
                    </p>
                  )}
                  {userCredits >= 5 && (
                    <p className="text-green-600 text-sm mt-2 flex items-center gap-1">
                      <CheckCircle className="h-4 w-4" />
                      You have enough credits!
                    </p>
                  )}
                </div>
              </div>

              {/* Action Buttons - Fixed at bottom */}
              <div className="flex gap-3 pt-4 border-t mt-4">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowNewScript(false);
                    resetForm();
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleConvert}
                  disabled={converting || !userCredits || userCredits < 5}
                  className="flex-1 bg-amber-900 hover:bg-amber-800 text-white font-semibold"
                  size="lg"
                >
                  {converting ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Generating Script...
                    </>
                  ) : (
                    <>
                      <Film className="h-5 w-5 mr-2" />
                      Generate Script
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Script Detail Dialog */}
          <Dialog open={showScriptDetail} onOpenChange={setShowScriptDetail}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <DialogTitle className="text-2xl flex items-center gap-2">
                      <FileText className="h-6 w-6 text-amber-600" />
                      {selectedScript?.title}
                    </DialogTitle>
                    <DialogDescription className="flex items-center gap-4 mt-2">
                      {getStatusBadge(selectedScript?.status)}
                      <span>•</span>
                      <span>{selectedScript?.scene_count || 0} Scenes</span>
                      <span>•</span>
                      <span>{selectedScript?.estimated_pages || 0} Pages</span>
                      <span>•</span>
                      <span>{selectedScript?.estimated_runtime}</span>
                    </DialogDescription>
                  </div>
                  <div className="flex gap-2">
                    {(selectedScript?.status === "generating" || selectedScript?.status === "failed") && (
                      <button
                        type="button"
                        className="inline-flex items-center justify-center rounded-md text-sm font-medium border shadow-sm h-8 px-3 bg-green-50 hover:bg-green-100 border-green-300 text-green-700"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log("Resume clicked");
                          handleResumeScript(selectedScript.id);
                        }}
                      >
                        <RefreshCw className="h-4 w-4 mr-1" />
                        Resume
                      </button>
                    )}
                    {selectedScript?.status === "generating" && (
                      <button
                        type="button"
                        className="inline-flex items-center justify-center rounded-md text-sm font-medium border border-input shadow-sm hover:bg-accent hover:text-accent-foreground h-8 px-3"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log("Refresh clicked");
                          refreshScript(selectedScript.id);
                        }}
                      >
                        <RefreshCw className="h-4 w-4 mr-1" />
                        Refresh
                      </button>
                    )}
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-md text-sm font-medium border shadow-sm h-8 px-3 bg-red-50 hover:bg-red-100 border-red-300 text-red-700"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log("Delete clicked");
                        handleDeleteScript(selectedScript?.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
              </DialogHeader>
              
              {selectedScript?.status === "generating" ? (
                <div className="flex-1 flex items-center justify-center py-8">
                  <div className="text-center w-full max-w-md">
                    <Loader2 className="h-16 w-16 animate-spin text-amber-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Generating Your Screenplay</h3>
                    
                    {/* Progress Bar */}
                    <div className="w-full bg-stone-200 rounded-full h-4 mb-2">
                      <div 
                        className="bg-amber-600 h-4 rounded-full transition-all duration-500"
                        style={{ width: `${selectedScript?.progress || 0}%` }}
                      />
                    </div>
                    <p className="text-2xl font-bold text-amber-700 mb-2">
                      {selectedScript?.progress || 0}%
                    </p>
                    
                    {/* Progress Message */}
                    <p className="text-stone-600 mb-2">
                      {selectedScript?.progress_message || "Starting..."}
                    </p>
                    
                    {/* Scene Progress */}
                    {selectedScript?.total_scenes > 0 && (
                      <p className="text-sm text-stone-500">
                        Scenes: {selectedScript?.scenes_completed || 0} / {selectedScript?.total_scenes}
                      </p>
                    )}
                    
                    <p className="text-xs text-stone-400 mt-4">
                      You can close this dialog and check back later.
                    </p>
                  </div>
                </div>
              ) : selectedScript?.status === "completed" ? (
                <div className="flex-1 overflow-hidden">
                  {/* Copy Section - with manual fallback */}
                  <div className="flex gap-2 justify-end mb-4 pb-4 border-b">
                    <Button
                      onClick={handleCopyScreenplay}
                      className="bg-green-700 hover:bg-green-600 text-white"
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Copy All to Clipboard
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        // Format text
                        let text = `${selectedScript.title?.toUpperCase() || 'UNTITLED'}\n${'='.repeat(selectedScript.title?.length || 8)}\n\nWritten by AI Screenplay Adaptation\n\n`;
                        for (const scene of selectedScript.scenes || []) {
                          text += `${scene.heading?.toUpperCase() || 'INT. LOCATION - DAY'}\n\n${scene.content || ''}\n\n${'─'.repeat(50)}\n\n`;
                        }
                        // Open in new window for manual copy
                        const newWindow = window.open('', '_blank', 'width=800,height=600');
                        if (newWindow) {
                          newWindow.document.write(`<html><head><title>Screenplay - ${selectedScript.title}</title></head><body><pre style="white-space:pre-wrap;font-family:Courier,monospace;padding:20px;">${text.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre></body></html>`);
                          newWindow.document.close();
                          toast.success("Opened in new window - use Ctrl+A then Ctrl+C to copy");
                        } else {
                          toast.error("Popup blocked - please allow popups");
                        }
                      }}
                    >
                      Open in New Window
                    </Button>
                  </div>
                  
                  <Tabs defaultValue="scenes" className="h-full flex flex-col">
                    <TabsList className="w-full justify-start">
                      <TabsTrigger value="scenes">Scenes ({selectedScript?.scene_count})</TabsTrigger>
                      <TabsTrigger value="characters">Characters ({selectedScript?.characters?.length})</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="scenes" className="flex-1 overflow-hidden mt-4">
                      <ScrollArea className="h-[50vh]">
                        <div className="space-y-4 pr-4">
                          {(selectedScript?.scenes || []).map((scene, idx) => (
                            <Card key={idx} className="border-stone-200">
                              <CardHeader className="py-3">
                                <CardTitle className="text-sm font-mono text-amber-800">
                                  Scene {scene.scene_number}: {scene.heading}
                                </CardTitle>
                              </CardHeader>
                              <CardContent className="py-2">
                                <pre className="text-sm font-mono whitespace-pre-wrap text-stone-700 max-h-48 overflow-y-auto">
                                  {scene.content}
                                </pre>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </ScrollArea>
                    </TabsContent>
                    
                    <TabsContent value="characters" className="flex-1 overflow-hidden mt-4">
                      <ScrollArea className="h-[50vh]">
                        <div className="space-y-2 pr-4">
                          {(selectedScript?.characters || []).map((char, idx) => (
                            <Card key={idx} className="border-stone-200">
                              <CardContent className="py-3 flex justify-between items-center">
                                <div>
                                  <p className="font-semibold">{char.name}</p>
                                  {char.description && (
                                    <p className="text-sm text-stone-600">{char.description}</p>
                                  )}
                                </div>
                                <Badge variant="outline">
                                  {char.dialogue_count} lines
                                </Badge>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </ScrollArea>
                    </TabsContent>
                  </Tabs>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center py-12">
                  <div className="text-center">
                    <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Generation Failed</h3>
                    <p className="text-stone-600">{selectedScript?.error_message || "An error occurred during generation."}</p>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default ScriptCreator;
